import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { PulseLoader } from 'react-spinners';

const AI_API_BASE = 'https://cmgrcbhs9b.execute-api.ap-south-1.amazonaws.com/direct';

function parseJsonList(value) {
    if (Array.isArray(value)) {
        return value.map((item) => String(item || '').trim()).filter(Boolean);
    }
    if (typeof value !== 'string' || !value.trim()) return [];
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
            return parsed.map((item) => String(item || '').trim()).filter(Boolean);
        }
    } catch {
        // fall through to comma split
    }
    return value
        .split(',')
        .map((item) => item.trim().replace(/^\[|\]$/g, '').replace(/^"|"$/g, ''))
        .filter(Boolean);
}

function cleanEmails(values) {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const unique = [];
    parseJsonList(values).forEach((raw) => {
        const email = String(raw).split('?')[0].trim().toLowerCase();
        if (emailPattern.test(email) && !unique.includes(email)) {
            unique.push(email);
        }
    });
    return unique;
}

function cleanPhones(values) {
    const unique = [];
    parseJsonList(values).forEach((raw) => {
        const phone = String(raw).replace(/[^\d+]/g, '');
        if (phone.length >= 8 && !unique.includes(phone)) {
            unique.push(phone);
        }
    });
    return unique;
}

function normalizeResults(results) {
    return (Array.isArray(results) ? results : []).map((item, index) => ({
        id: `${item.link || 'result'}-${index}`,
        title: item.title || 'Untitled result',
        link: item.link || '',
        phones: cleanPhones(item.Phones || item.phones),
        emails: cleanEmails(item.Email || item.emails)
    }));
}

async function readApiJson(response) {
    const text = await response.text();
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

const AiChat = () => {
    const [query, setQuery] = useState('');
    const [history, setHistory] = useState([]);
    const [messages, setMessages] = useState([]);
    const [activeSearchId, setActiveSearchId] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(true);
    const chatEndRef = useRef(null);
    const messagesRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        document.body.classList.add('ai-chat-active');
        document.documentElement.classList.add('ai-chat-active');
        return () => {
            document.body.classList.remove('ai-chat-active');
            document.documentElement.classList.remove('ai-chat-active');
        };
    }, []);

    const scrollToBottom = () => {
        const el = messagesRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isSearching]);

    const loadHistory = useCallback(async () => {
        setIsHistoryLoading(true);
        try {
            const response = await fetch(`${AI_API_BASE}/history`);
            const data = await readApiJson(response);
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to load history');
            }
            setHistory(Array.isArray(data) ? data : data?.history || []);
        } catch (error) {
            console.error('AI history error:', error);
            toast.error(error.message || 'Failed to load search history', { autoClose: 2000 });
        } finally {
            setIsHistoryLoading(false);
        }
    }, []);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const showSearchInChat = (searchQuery, results, searchId) => {
        setActiveSearchId(searchId || '');
        setMessages([
            {
                id: `user-${searchId || Date.now()}`,
                role: 'user',
                text: searchQuery
            },
            {
                id: `ai-${searchId || Date.now()}`,
                role: 'assistant',
                text: results.length
                    ? `Found ${results.length} result${results.length === 1 ? '' : 's'} for "${searchQuery}".`
                    : `No results found for "${searchQuery}".`,
                results
            }
        ]);
    };

    const handleSearch = async (event) => {
        event.preventDefault();
        const trimmed = query.trim();
        if (!trimmed || isSearching) return;

        setIsSearching(true);
        setMessages((prev) => [
            ...prev,
            { id: `user-${Date.now()}`, role: 'user', text: trimmed }
        ]);
        setQuery('');

        try {
            const response = await fetch(`${AI_API_BASE}/scrape`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ query: trimmed })
            });
            const data = await readApiJson(response);
            if (!response.ok) {
                throw new Error(data?.message || data?.detail || 'Search failed');
            }
            const results = normalizeResults(data?.results);
            const searchId = data?.search_id || '';
            setActiveSearchId(searchId);
            setMessages((prev) => [
                ...prev,
                {
                    id: `ai-${searchId || Date.now()}`,
                    role: 'assistant',
                    text: results.length
                        ? `Found ${results.length} result${results.length === 1 ? '' : 's'} for "${trimmed}".`
                        : `No results found for "${trimmed}".`,
                    results
                }
            ]);
            loadHistory();
        } catch (error) {
            console.error('AI scrape error:', error);
            setMessages((prev) => [
                ...prev,
                {
                    id: `ai-error-${Date.now()}`,
                    role: 'assistant',
                    text: error.message || 'Something went wrong while searching.',
                    results: []
                }
            ]);
            toast.error(error.message || 'Search failed', { autoClose: 2000 });
        } finally {
            setIsSearching(false);
            inputRef.current?.focus();
        }
    };

    const handleSelectHistory = async (item) => {
        if (!item?.id || isSearching) return;
        setIsSearching(true);
        setActiveSearchId(item.id);
        try {
            const response = await fetch(`${AI_API_BASE}/history/${item.id}`);
            const data = await readApiJson(response);
            if (!response.ok) {
                throw new Error(data?.message || 'Failed to load search');
            }
            const results = normalizeResults(data?.results);
            showSearchInChat(data?.query || item.query, results, data?.id || item.id);
        } catch (error) {
            console.error('AI history detail error:', error);
            toast.error(error.message || 'Failed to open history item', { autoClose: 2000 });
        } finally {
            setIsSearching(false);
        }
    };

    const handleNewSearch = () => {
        setMessages([]);
        setActiveSearchId('');
        setQuery('');
        inputRef.current?.focus();
    };

    const groupedHistory = useMemo(() => history, [history]);

    return (
        <div className="ai-chat-page">
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">AI Search</h1>
                        </div>
                    </div>
                </div>
            </div>
            <div className="content">
                <div className="container-fluid ai-chat-page-body">
                    <div className="ai-chat-shell">
                        <aside className={`ai-chat-history ${historyOpen ? '' : 'is-collapsed'}`}>
                            <div className="ai-chat-history-header">
                                <button type="button" className="btn btn-blue btn-sm btn-block" onClick={handleNewSearch}>
                                    <i className="fa-solid fa-plus mr-1" />
                                    New search
                                </button>
                            </div>
                            <div className="ai-chat-history-list">
                                {isHistoryLoading ? (
                                    <div className="ai-chat-empty">Loading history...</div>
                                ) : groupedHistory.length === 0 ? (
                                    <div className="ai-chat-empty">No searches yet</div>
                                ) : (
                                    groupedHistory.map((item) => (
                                        <button
                                            type="button"
                                            key={item.id}
                                            className={`ai-chat-history-item ${activeSearchId === item.id ? 'is-active' : ''}`}
                                            onClick={() => handleSelectHistory(item)}
                                        >
                                            <span className="ai-chat-history-query">{item.query || 'Untitled search'}</span>
                                            <span className="ai-chat-history-count">{item.result_count || 0} results</span>
                                        </button>
                                    ))
                                )}
                            </div>
                        </aside>

                        <section className="ai-chat-main">
                            <button
                                type="button"
                                className="ai-chat-toggle"
                                onClick={() => setHistoryOpen((open) => !open)}
                                title={historyOpen ? 'Hide history' : 'Show history'}
                            >
                                <i className={`fa-solid ${historyOpen ? 'fa-angles-left' : 'fa-angles-right'}`} />
                            </button>

                            <div
                                className={`ai-chat-messages ${messages.length === 0 && !isSearching ? 'is-empty' : ''}`}
                                ref={messagesRef}
                            >
                                {messages.length === 0 && !isSearching ? (
                                    <div className="ai-chat-welcome">
                                        <div className="ai-chat-welcome-icon">
                                            <i className="fa-solid fa-robot" />
                                        </div>
                                        <h3>Ask AI to find suppliers</h3>
                                        <p>Search the web for dealers, emails and phone numbers.</p>
                                    </div>
                                ) : (
                                    messages.map((message) => (
                                        <div key={message.id} className={`ai-chat-bubble ai-chat-bubble-${message.role}`}>
                                            <div className="ai-chat-bubble-text">{message.text}</div>
                                            {message.role === 'assistant' && message.results?.length > 0 ? (
                                                <div className="ai-chat-results">
                                                    {message.results.map((result) => (
                                                        <article key={result.id} className="ai-chat-result-card">
                                                            <h4>{result.title}</h4>
                                                            {result.link ? (
                                                                <a href={result.link} target="_blank" rel="noreferrer">
                                                                    {result.link}
                                                                </a>
                                                            ) : null}
                                                            <div className="ai-chat-result-meta">
                                                                <div>
                                                                    <strong>Phones</strong>
                                                                    <p>
                                                                        {result.phones.length
                                                                            ? result.phones.join(', ')
                                                                            : '-'}
                                                                    </p>
                                                                </div>
                                                                <div>
                                                                    <strong>Email</strong>
                                                                    <p>
                                                                        {result.emails.length
                                                                            ? result.emails.map((email, index) => (
                                                                                <span key={email}>
                                                                                    {index > 0 ? ', ' : ''}
                                                                                    <a href={`mailto:${email}`}>{email}</a>
                                                                                </span>
                                                                            ))
                                                                            : '-'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </article>
                                                    ))}
                                                </div>
                                            ) : null}
                                        </div>
                                    ))
                                )}
                                {isSearching ? (
                                    <div className="ai-chat-bubble ai-chat-bubble-assistant d-flex align-items-center">
                                        <PulseLoader color="#17a2b8" size={8} />
                                        <span className="ml-2">Searching...</span>
                                    </div>
                                ) : null}
                                <div ref={chatEndRef} />
                            </div>

                            <form className="ai-chat-composer" onSubmit={handleSearch}>
                                <input
                                    ref={inputRef}
                                    type="text"
                                    className="form-control"
                                    placeholder="Ask anything"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    disabled={isSearching}
                                />
                                <button
                                    type="submit"
                                    className="btn btn-info"
                                    disabled={!query.trim() || isSearching}
                                >
                                    <i className="fa-solid fa-paper-plane" />
                                </button>
                            </form>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AiChat;
